import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react'
import { useLocation } from 'react-router'

import { ApolloQueryResult } from '@apollo/client'
import * as Sentry from '@sentry/react'
import { useEditor } from '@toasttab/sites-components'

import {
  CartFulfillmentInput,
  CartQuery,
  FulfillmentType,
  OrderSource,
  useAddToCartMutation,
  useApplyPromoCodeMutation,
  useAttachGuestToCartMutation,
  useCartQuery,
  useDeleteFromCartMutation,
  useEditCartItemMutation,
  useRemovePromoCodeMutation,
  useReorderMutation
} from 'src/apollo/onlineOrdering'
import { useTracker, useThrottledTracker } from 'src/lib/js/hooks/useTracker'
import { TrackingEvents } from 'src/shared/components/common/tracking/TrackingEvents'
import { cleanDeliveryInfo } from 'src/public/components/online_ordering/fulfillmentUtils'
import { useCustomer } from 'src/public/components/online_ordering/CustomerContextCommon'
import { useOOClient } from 'src/shared/components/common/oo_client_provider/OOClientProvider'

import { useFlags } from 'shared/components/common/feature_flags/useFlags'
import { useRestaurant } from 'shared/components/common/restaurant_context/RestaurantContext'

import { useSpi } from 'public/components/default_template/online_ordering/checkout/payment/useSpi'
import { Channel } from 'public/js/siteUtilities'

import ToastProduct, { isToastAppProduct } from './ToastProduct'
import { getDigitalSurface } from './attributionUtils'
import { useCartStorage } from './useCartStorage'
import {
  AddCartData,
  Cart,
  CartData,
  CartOrder,
  WrappedModifier
} from './types'
import {
  EditSource,
  ItemSource
} from '../default_template/online_ordering/cart/cartUtils'
import {
  calculateSubtotal,
  modifierToSelectionInput
} from 'public/components/online_ordering/modifierUtils'

export const getOrderSource = (toastProduct: ToastProduct) => {
  if (
    toastProduct === ToastProduct.ToastLocal ||
    toastProduct === ToastProduct.FreeOOToastLocal ||
    toastProduct === ToastProduct.ToastApp
  ) {
    return OrderSource.Local
  } else if (toastProduct === ToastProduct.OOBasic) {
    return OrderSource.Online
  } else if (toastProduct === ToastProduct.OOEcommerce) {
    return OrderSource.EcommerceOo
  }

  return OrderSource.Boo
}

type ErrorType =
  | 'CartModificationError'
  | 'CartOutOfStockError'
  | 'CartError'
  | 'ApplyPromoCodeError'
  | 'ReorderError'
  | 'ApolloError'
export type CartErrorType = {
  kind: ErrorType
  message: string
}

export type Surcharges =
  | {
      [key: string]: {
        surchargeAmount?: number
        surchargeRate?: number
      }
    }
  | null
  | undefined

export type CartContextType = {
  cartGuid?: string | null
  // Not to be confused with cart.cartSource, which indicates if the cart is backed by online-ordering or do-carts,
  // cartReferrer indicates if a third-party application, e.g. Google Ask Maps, built the cart via a Toast API or service
  // and then directed the guest to online ordering checkout to pay for the order.
  cartReferrer?: string | null
  refetchCart: (
    variables?: {
      guid?: string | null
      totalGiftCardBalance?: number | null
    } | null
  ) => Promise<ApolloQueryResult<CartQuery>>
  clearCart: () => void
  replaceCart: (newCartGuid: string) => void
  addToCart: (
    item: WrappedModifier,
    quantity: number,
    fulfillmentData?: CartFulfillmentInput,
    trackData?: any,
    channelGuid?: string,
    itemSource?: ItemSource
  ) => Promise<null | ErrorType>
  editCartItem: (
    item: WrappedModifier,
    quantity: number,
    selectionGuid: string,
    itemSource?: ItemSource,
    editSource?: EditSource
  ) => Promise<null | ErrorType>
  deleteFromCart: (itemGuid: string) => Promise<void>
  reorder: (
    orderGuid: string,
    restaurantGuid: string,
    fulfillment?: CartFulfillmentInput
  ) => Promise<void>
  subtotal?: number | null
  applyPromoCode: (promoCode: string) => Promise<null | ErrorType>
  removePromoCode: () => Promise<null | ErrorType>
  loadingCart: boolean
  cart?: Cart | null
  channel?: Channel
  restaurantGuid: string
  error?: CartErrorType | null
  clearError: () => void
  surcharges: Surcharges
  // timestamp of the last selection modification in the cart so we can react to changes
  lastOrderSelectionChange: Date
}

export const CartContext = createContext<CartContextType | undefined>(undefined)

export type CartRestaurant = Cart['restaurant'] & { __typename: 'Restaurant' }
export type Location = CartRestaurant['location']

type Props = {
  channel?: Channel
  paramCartGuid?: string | null
  paramSource?: string | null
}

const WrappedCartContextProvider = ({
  channel = Channel.OO,
  paramCartGuid,
  paramSource,
  children
}: React.PropsWithChildren<Props>) => {
  const cartStorage = useCartStorage()
  const { selectedLocation, toastProduct } = useRestaurant()
  const [loading, setLoading] = useState(false)
  const [loadingCartGuid, setLoadingCartGuid] = useState(true)
  const [error, setError] = useState<null | CartErrorType>(null)
  const [cartGuid, setCartGuid] = useState<string | null>(null)
  const unthrottledTracker = useTracker()
  const { track } = useThrottledTracker()
  const ooClient = useOOClient()
  const [addToCartMutation] = useAddToCartMutation({ client: ooClient })
  const [deleteFromCartMutation] = useDeleteFromCartMutation({
    client: ooClient
  })
  const [editCartItemMutation] = useEditCartItemMutation({ client: ooClient })
  const [applyPromoCodeMutation] = useApplyPromoCodeMutation({
    client: ooClient
  })
  const [removePromoCodeMutation] = useRemovePromoCodeMutation({
    client: ooClient
  })
  const [reorderMutation] = useReorderMutation({ client: ooClient })
  const [attachGuestToCartMutation] = useAttachGuestToCartMutation({
    client: ooClient
  })
  const { isLoggedIn } = useCustomer()
  const { nv1ToastAppFreeDeliveryOrdering } = useFlags()
  const { spiSurchargingEnabled } = useSpi()
  const [lastOrderSelectionChange, setLastOrderSelectionChange] =
    useState<Date>(new Date())
  const lastOrderRef = useRef<CartOrder | null | undefined>(null)
  const updateQueueRef = useRef<Promise<any>>(Promise.resolve())
  const attachedCartGuidRef = useRef<string | null>(null)

  const restaurantGuid = selectedLocation.externalId

  const clearCart = useCallback(() => {
    if (cartStorage) {
      cartStorage.clearCartGuid(channel, restaurantGuid)
    }
    setCartGuid(null)
  }, [cartStorage, channel, restaurantGuid, setCartGuid])

  const clearError = useCallback(() => {
    setError(null)
  }, [])

  useEffect(() => {
    if (cartStorage) {
      const storedCartGuid = cartStorage.getCartGuid(channel, restaurantGuid)
      const cartGuid = paramCartGuid ?? storedCartGuid
      if (cartGuid) {
        setCartGuid(cartGuid)
      } else {
        setCartGuid(null)
      }
      setLoadingCartGuid(false)
    }
  }, [channel, restaurantGuid, paramCartGuid, cartStorage])

  const {
    data,
    loading: loadingData,
    error: reqError,
    refetch
  } = useCartQuery({
    ssr: false,
    skip: !cartGuid,
    variables: { guid: cartGuid || '' },
    notifyOnNetworkStatusChange: true,
    client: ooClient
  })

  useEffect(() => {
    if (reqError) {
      setError({
        kind: 'ApolloError',
        message: reqError.message
      })
    }
  }, [reqError])

  useEffect(() => {
    if (data?.cartV2?.__typename === 'CartError') {
      setError({
        kind: data?.cartV2.__typename,
        message: data?.cartV2.message
      })
      clearCart()
    }
  }, [data?.cartV2, clearCart])

  // A cart started before the guest signed in belongs to no guest, so the
  // backend cannot apply a guest-scoped offer such as Toast-funded free delivery
  // to it. Attaching the guest reprices the cart under them. Scoped to the one
  // channel that can offer a guest-scoped offer today rather than repricing
  // every logged in cart.
  const attachGuestEnabled =
    nv1ToastAppFreeDeliveryOrdering && isToastAppProduct(toastProduct)

  useEffect(() => {
    if (!isLoggedIn || !attachGuestEnabled) {
      // Cleared on logout so the next guest to sign in gets attached too.
      attachedCartGuidRef.current = null
      return
    }

    if (!cartGuid || attachedCartGuidRef.current === cartGuid) {
      return
    }

    // Once per cart per mounted provider, so a failure is picked up when the
    // guest moves on to the checkout page, which mounts its own provider.
    attachedCartGuidRef.current = cartGuid
    attachGuestToCartMutation({ variables: { input: { cartGuid } } })
      .then(({ data }) => {
        const result = data?.attachGuestToCart

        // Nothing to surface to the guest: the cart is still usable, it just did
        // not pick up any guest-scoped offer. Logged because this is otherwise
        // indistinguishable from a guest who had no offer to apply.
        if (!result || result.__typename === 'AttachGuestToCartError') {
          Sentry.captureMessage('Failed to attach the guest to their cart', {
            level: 'warning',
            extra: { cartGuid, code: result?.code }
          })
          return
        }

        // Attaching reprices the cart, so resync even when the reprice hit an
        // out of stock item or another modification error.
        refetch()
      })
      .catch((err) => {
        Sentry.captureException(err)
      })
  }, [
    isLoggedIn,
    attachGuestEnabled,
    cartGuid,
    attachGuestToCartMutation,
    refetch
  ])

  const replaceCart = useCallback(
    (newCartGuid: string) => {
      if (cartStorage) {
        cartStorage.setCartGuid(channel, restaurantGuid, newCartGuid)
      }
      setCartGuid(newCartGuid)
      refetch({ guid: newCartGuid })
    },
    [cartStorage, channel, restaurantGuid, setCartGuid, refetch]
  )

  const cartData: CartData = data?.cartV2 as CartData

  useEffect(() => {
    const cartSource = cartData?.cart?.cartSource

    // Latch the cartSource once we have it in case we get an error later. It won't change for the lifetime of the cart.
    if (cartSource) {
      unthrottledTracker.register({ cartSource })
    }
  }, [unthrottledTracker, cartData?.cart?.cartSource])

  useEffect(() => {
    const order = cartData?.cart?.order
    if (hasOrderSelectionChanged(order, lastOrderRef.current)) {
      lastOrderRef.current = order
      setLastOrderSelectionChange(new Date())
    }
  }, [cartData?.cart?.order])

  const surcharges: Surcharges = useMemo(() => {
    if (spiSurchargingEnabled && cartData?.cart?.order?.surcharges) {
      return cartData.cart.order.surcharges.reduce(
        (
          acc: {
            [key: string]: { surchargeAmount: number; surchargeRate: number }
          },
          surchargeSummary
        ) => {
          surchargeSummary?.paymentTypes?.forEach((paymentType: string) => {
            if (surchargeSummary.surchargePreTaxAmount) {
              acc[paymentType] = {
                surchargeAmount:
                  surchargeSummary.surchargePreTaxAmount +
                  surchargeSummary.surchargeTaxAmount,
                surchargeRate: surchargeSummary.surchargeRate
              }
            }
          })
          return acc
        },
        {}
      )
    }
    return null
  }, [cartData?.cart?.order?.surcharges, spiSurchargingEnabled])

  const addToCart = useCallback(
    async (
      item: WrappedModifier,
      quantity: number,
      fulfillmentData?: CartFulfillmentInput,
      trackData?: any,
      channelGuid?: string,
      itemSource?: ItemSource
    ) => {
      // this should never happen, since we don't show the cart if ordering is unavailable
      if (!fulfillmentData && channel !== Channel.ECOMM) {
        return null
      }

      setLoading(true)

      // Clean fulfillmentData to ensure deliveryInfo only contains expected fields
      const cleanedFulfillmentData = fulfillmentData
        ? {
            ...fulfillmentData,
            deliveryInfo: cleanDeliveryInfo(fulfillmentData.deliveryInfo)
          }
        : { fulfillmentType: FulfillmentType.Asap }

      const { data } = await addToCartMutation({
        variables: {
          input: {
            cartGuid,
            createCartInput: cartGuid
              ? null
              : {
                  restaurantGuid: restaurantGuid,
                  orderSource: getOrderSource(toastProduct),
                  cartFulfillmentInput: cleanedFulfillmentData,
                  digitalSurface: getDigitalSurface(toastProduct, channel),
                  channelGuid
                },
            selection: modifierToSelectionInput({
              ...item,
              quantity
            })
          }
        }
      })

      let shouldRefetch = !!cartGuid

      if (data?.addItemToCartV2?.__typename === 'CartResponse') {
        const cartData: AddCartData = data?.addItemToCartV2 as AddCartData
        if (cartData.cart.guid !== cartGuid) {
          if (cartStorage) {
            cartStorage.setCartGuid(channel, restaurantGuid, cartData.cart.guid)
          }
          setCartGuid(cartData.cart.guid)
          clearError()
          // Skip refetch — useCartQuery will re-run automatically with the new GUID
          shouldRefetch = false
        }
      }

      if (shouldRefetch) {
        refetch()
      }
      setLoading(false)

      if (
        data?.addItemToCartV2?.__typename === 'CartModificationError' ||
        data?.addItemToCartV2?.__typename === 'CartOutOfStockError'
      ) {
        setError({
          kind: data?.addItemToCartV2.__typename,
          message: data?.addItemToCartV2?.message
        })
        return data?.addItemToCartV2?.__typename
      }

      const {
        cart: { cartSource }
      } = data?.addItemToCartV2 as AddCartData

      // Ensure we set the user property if we just created a new cart so we're not racing
      // it getting set via refetch() above, which might be too late for this track call.
      unthrottledTracker.register({ cartSource })

      track(TrackingEvents.ADDED_ITEM_TO_CART, {
        ...trackData,
        cartGuid: cartGuid,
        cartSource,
        diningOption: cartData?.cart?.diningOptionBehavior,
        deliveryProvider: cartData?.cart?.deliveryProviderInfo?.provider,
        validDeliveryAddressPresent: !!cartData?.cart?.order?.deliveryInfo,
        itemSource,
        fractionalQuantityItem: item.usesFractionalQuantity
      })

      return null
    },
    [
      cartStorage,
      unthrottledTracker,
      track,
      cartGuid,
      addToCartMutation,
      restaurantGuid,
      refetch,
      toastProduct,
      channel,
      cartData
    ]
  )

  const editCartItem = useCallback(
    async (
      item: WrappedModifier,
      quantity: number,
      selectionGuid: string,
      itemSource?: ItemSource,
      editSource?: EditSource
    ) => {
      // this shouldn't be possible
      if (!cartGuid) {
        return null
      }

      // Queue the update to ensure sequential execution
      const queuedUpdate = updateQueueRef.current.then(async () => {
        setLoading(true)

        try {
          const { data } = await editCartItemMutation({
            variables: {
              input: {
                cartGuid,
                selectionGuid,
                selection: modifierToSelectionInput({
                  ...item,
                  quantity
                })
              }
            }
          })

          await refetch()
          setLoading(false)

          if (
            data?.editItemInCartV2.__typename === 'CartModificationError' ||
            data?.editItemInCartV2?.__typename === 'CartOutOfStockError'
          ) {
            setError({
              kind: data?.editItemInCartV2.__typename,
              message: data?.editItemInCartV2?.message
            })
            return data?.editItemInCartV2?.__typename
          }

          const {
            cart: { cartSource }
          } = data?.editItemInCartV2 as AddCartData

          track(TrackingEvents.EDITED_ITEM_IN_CART, {
            cartGuid: cartGuid,
            cartSource,
            diningOption: cartData?.cart?.diningOptionBehavior,
            deliveryProvider: cartData?.cart?.deliveryProviderInfo?.provider,
            validDeliveryAddressPresent: !!cartData?.cart?.order?.deliveryInfo,
            itemSource,
            fractionalQuantityItem: item.usesFractionalQuantity,
            editSource
          })

          return null
        } catch (error) {
          setLoading(false)
          console.error('Failed to edit cart item:', error)
          throw error
        }
      })

      // Update the queue reference, but don't let errors block future operations
      updateQueueRef.current = queuedUpdate.catch(() => {
        // Swallow the error in the queue chain so it doesn't block subsequent operations
      })

      // Return the actual promise so the caller can handle errors
      return queuedUpdate
    },
    [cartData, cartGuid, editCartItemMutation, refetch, track]
  )

  const deleteFromCart = useCallback(
    async (itemGuid: string) => {
      if (!cartGuid) {
        return
      }

      // Queue the delete to ensure sequential execution
      const queuedDelete = updateQueueRef.current.then(async () => {
        setLoading(true)

        try {
          await deleteFromCartMutation({
            variables: {
              input: {
                cartGuid,
                selectionGuid: itemGuid
              }
            }
          })

          // Not necessary, could be replaced with cart returned from mutation
          await refetch()
          setLoading(false)
        } catch (error) {
          setLoading(false)
          throw error
        }
      })

      // Update the queue reference, but don't let errors block future operations
      updateQueueRef.current = queuedDelete.catch(() => {
        // Swallow the error in the queue chain so it doesn't block subsequent operations
      })

      // Return the actual promise so the caller can handle errors
      return queuedDelete
    },
    [cartGuid, deleteFromCartMutation, refetch]
  )

  const applyPromoCode = useCallback(
    async (promoCode: string) => {
      // this shouldn't be possible
      if (!cartGuid) {
        return null
      }

      setLoading(true)
      const { data } = await applyPromoCodeMutation({
        variables: { input: { cartGuid, promoCode } }
      })

      if (data?.applyPromoCodeV3.__typename === 'ApplyPromoCodeError') {
        setLoading(false)
        return data?.applyPromoCodeV3.__typename
      }

      refetch()
      setLoading(false)
      return null
    },
    [cartGuid, applyPromoCodeMutation, refetch]
  )

  const removePromoCode = useCallback(async () => {
    // this shouldn't be possible
    if (!cartGuid) {
      return null
    }

    setLoading(true)
    const { data } = await removePromoCodeMutation({
      variables: { input: { cartGuid } }
    })

    if (
      data?.removePromoCodeV2.__typename === 'CartModificationError' ||
      data?.removePromoCodeV2.__typename === 'CartOutOfStockError'
    ) {
      setLoading(false)
      return data?.removePromoCodeV2.__typename
    }

    refetch()
    setLoading(false)
    return null
  }, [cartGuid, removePromoCodeMutation, refetch])

  const reorder = useCallback(
    async (
      orderGuid: string,
      restaurantGuid: string,
      fulfillment?: CartFulfillmentInput
    ) => {
      setLoading(true)

      // Clean fulfillment data to ensure deliveryInfo only contains expected fields
      const cleanedFulfillment = fulfillment
        ? {
            ...fulfillment,
            deliveryInfo: cleanDeliveryInfo(fulfillment.deliveryInfo)
          }
        : undefined

      const { data } = await reorderMutation({
        variables: {
          input: {
            orderGuid,
            restaurantGuid,
            cartFulfillmentInput: cleanedFulfillment,
            orderSource: getOrderSource(toastProduct)
          }
        }
      })

      if (data?.reorderV2?.__typename === 'ReorderResponse') {
        if (cartStorage) {
          cartStorage.setCartGuid(
            channel,
            restaurantGuid,
            data.reorderV2.cart.guid
          )
        }
        setCartGuid(data.reorderV2.cart.guid)
      }

      if (data?.reorderV2?.__typename === 'ReorderError') {
        setError({
          kind: data?.reorderV2.__typename,
          message: data?.reorderV2?.message
        })
      }

      setLoading(false)
      return
    },
    [cartStorage, reorderMutation, channel, restaurantGuid, toastProduct]
  )

  const subtotal = useMemo(
    () => calculateSubtotal(cartData?.cart?.order),
    [cartData]
  )

  return (
    <CartContext.Provider
      value={{
        cartGuid,
        cartReferrer: paramSource,
        refetchCart: refetch,
        clearCart,
        replaceCart,
        addToCart,
        deleteFromCart,
        editCartItem,
        reorder,
        subtotal,
        applyPromoCode,
        removePromoCode,
        loadingCart: loading || loadingCartGuid || loadingData,
        cart: cartData?.cart,
        channel,
        restaurantGuid: restaurantGuid,
        error,
        clearError,
        surcharges,
        lastOrderSelectionChange
      }}
    >
      {children}
    </CartContext.Provider>
  )
}

const CartContextProviderWithParams = (
  props: React.PropsWithChildren<Omit<Props, 'paramCartGuid' | 'paramSource'>>
) => {
  const { search } = useLocation()
  const params = useMemo(() => new URLSearchParams(search), [search])

  return (
    <WrappedCartContextProvider
      {...props}
      paramCartGuid={params.get('cartGuid')}
      paramSource={params.get('source')}
    />
  )
}

export const CartContextProvider = (
  props: React.PropsWithChildren<Omit<Props, 'paramCartGuid' | 'paramSource'>>
) => {
  const { isEditor } = useEditor()

  if (isEditor) {
    return <WrappedCartContextProvider {...props} />
  } else {
    return <CartContextProviderWithParams {...props} />
  }
}

export const useCart = () => {
  const context = useContext(CartContext)
  if (!context) {
    throw new Error('useCart must be used within a CartContextProvider')
  }

  return context
}

export const useOptionalCart = () => {
  const context = useContext(CartContext)

  return context
}

const hasOrderSelectionChanged = (
  order?: CartOrder,
  lastOrder?: CartOrder
): boolean => {
  // We can rely on length to short-circuit the comparison because you can not add and remove selections in the same operation
  // Length covers adding, removing and quantity changes while preDiscountItemsSubtotals covers quantity and modifier changes
  return (
    order?.numberOfSelections !== lastOrder?.numberOfSelections ||
    order?.preDiscountItemsSubtotal !== lastOrder?.preDiscountItemsSubtotal
  )
}
